/** Minimal media shape for About gallery identity / removal matching. */
export interface GroupMediaIdentity {
  id?: string
  url?: string
  _id?: string
  licenseId?: string
}

const nonEmpty = (value: unknown): value is string => typeof value === 'string' && value.length > 0

const isMediaIdentity = (value: unknown): value is GroupMediaIdentity => typeof value === 'object' && value !== null

const getIdentityValues = (media: GroupMediaIdentity): string[] => [media.licenseId, media.id, media._id].filter(nonEmpty)

/** Stable id for server payloads: prefer licenseId, then id/_id, then url for legacy rows. */
export const resolveGroupMediaId = (media: GroupMediaIdentity): string =>
  [media.licenseId, media.id, media._id, media.url].find(nonEmpty) ?? ''

/**
 * Match media for delete/replace.
 * Compares any stable identifier (licenseId, id, _id); falls back to url when identifiers are absent.
 */
export const isSameGroupMedia = (a: unknown, b: unknown): boolean => {
  if (!isMediaIdentity(a) || !isMediaIdentity(b)) return false

  const aIds = getIdentityValues(a)
  const bIds = getIdentityValues(b)
  if (aIds.length > 0 && bIds.length > 0) {
    return aIds.some(id => bIds.includes(id))
  }

  return Boolean(a.url && b.url && a.url === b.url)
}

export const findGroupMediaIndex = <T extends GroupMediaIdentity>(list: T[], target: GroupMediaIdentity): number =>
  list.findIndex(media => isSameGroupMedia(media, target))

export const filterRemovedGroupMedia = <T extends GroupMediaIdentity>(existing: T[], toRemove: GroupMediaIdentity[]): T[] =>
  existing.filter(media => !toRemove.some(removed => isSameGroupMedia(media, removed)))
